import React from 'react';
import { Box, Container, Grid, Typography, Link, IconButton } from '@mui/material';
import { Logo } from './Logo';
import { styled } from '@mui/material/styles';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FacebookIcon from '@mui/icons-material/Facebook';

const StyledFooter = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#0A0A0F' : '#ffffff',
  borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(138, 43, 226, 0.2)' : 'rgba(138, 43, 226, 0.1)'}`,
  width: '100%',
  position: 'relative',
  padding: theme.spacing(6, 0),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4, 0),
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: theme => theme.palette.mode === 'dark' ? `
      radial-gradient(circle at center, ${theme.palette.primary.main}15 0%, transparent 70%),
      repeating-linear-gradient(45deg, ${theme.palette.primary.main}08 0px, ${theme.palette.primary.main}08 1px, transparent 1px, transparent 10px),
      repeating-linear-gradient(-45deg, ${theme.palette.primary.main}08 0px, ${theme.palette.primary.main}08 1px, transparent 1px, transparent 10px)
    ` : `
      radial-gradient(circle at center, #00508215 0%, transparent 70%),
      repeating-linear-gradient(45deg, #00508208 0px, #00508208 1px, transparent 1px, transparent 10px),
      repeating-linear-gradient(-45deg, #00508208 0px, #00508208 1px, transparent 1px, transparent 10px)
    `,
    backgroundSize: '100% 100%, 20px 20px, 20px 20px',
    opacity: 0.1,
    zIndex: 0,
  },
  '& .MuiGrid-item': {
    paddingBottom: theme.spacing(3),
  },
  '& .MuiContainer-root': {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: theme.spacing(0, 3),
  },
  '& .MuiGrid-container': {
    backgroundColor: 'transparent',
    margin: 0,
    width: '100%',
    position: 'relative',
    zIndex: 1,
  },
  '& .MuiBox-root': {
    backgroundColor: 'transparent',
  },
}));

const FooterLink = styled(Link)(({ theme }) => ({
  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
  textDecoration: 'none',
  fontSize: '0.875rem',
  transition: 'all 0.2s ease-in-out',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    width: '0%',
    height: '2px',
    bottom: -2,
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: theme.palette.primary.main,
    transition: 'width 0.3s ease-in-out',
    opacity: 0.7,
  },
  '&:hover': {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&::after': {
      width: '100%',
    },
  },
}));

const SocialButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
  padding: '8px',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.main + '20',
    transform: 'translateY(-2px)',
  },
}));

const Footer = () => {
  return (
    <StyledFooter component="footer">
      <Container maxWidth="lg">
        <Grid container spacing={6}>
          <Grid item xs={12} sm={4}>
            <Box>
              <Box sx={{ mb: 2 }}>
                <Logo />
              </Box>
              <Typography 
                variant="body2" 
                color="textSecondary"
                sx={{
                  opacity: theme => theme.palette.mode === 'dark' ? 0.7 : 0.6,
                  maxWidth: '300px',
                  lineHeight: 1.6
                }}
              >
                Yapay zeka destekli profesyonel mimari görselleştirme ve düzeleme araçları.
              </Typography>
              <Box sx={{ mt: 2 }}>
                <SocialButton 
                  aria-label="linkedin" 
                  component="a" 
                  href="https://www.linkedin.com/company/sanal-yapi/?viewAsMember=true"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <LinkedInIcon />
                </SocialButton>
                <SocialButton 
                  aria-label="facebook" 
                  component="a" 
                  href="https://www.facebook.com/profile.php?id=61573501597544"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FacebookIcon />
                </SocialButton>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Typography variant="subtitle1" color="textPrimary" gutterBottom sx={{ fontWeight: 600 }}>
              Ürün
            </Typography>
            <Box display="flex" flexDirection="column" gap={1}>
              <FooterLink href="https://www.sanalyapi.com/#features">Özellikler</FooterLink>
              <FooterLink href="https://www.sanalyapi.com/#pricing">Fiyatlandırma</FooterLink>
            </Box>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Typography variant="subtitle1" color="textPrimary" gutterBottom sx={{ fontWeight: 600 }}>
              Şirket
            </Typography>
            <Box display="flex" flexDirection="column" gap={1}>
              <FooterLink href="mailto:info@sanalyapi.com">İletişim</FooterLink>
            </Box>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Typography variant="subtitle1" color="textPrimary" gutterBottom sx={{ fontWeight: 600 }}>
              Kaynaklar
            </Typography>
            <Box display="flex" flexDirection="column" gap={1}>
              <FooterLink href="https://www.sanalyapi.com/#faq">Yardım Merkezi</FooterLink>
              <FooterLink href="mailto:info@sanalyapi.com">API</FooterLink>
            </Box>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Typography variant="subtitle1" color="textPrimary" gutterBottom sx={{ fontWeight: 600 }}>
              Yasal
            </Typography>
            <Box display="flex" flexDirection="column" gap={1} sx={{ mb: 2 }}>
              <FooterLink href="https://www.sanalyapi.com/privacy-policy">Gizlilik</FooterLink>
              <FooterLink href="https://www.sanalyapi.com/terms">Kullanım Şartları</FooterLink>
              <FooterLink href="https://www.sanalyapi.com/security-policy">Güvenlik</FooterLink>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </StyledFooter>
  );
};

export default Footer;
